import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Notification, NotificationContextType } from '../types/notifications';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, currentTeam } = useAuth();


  const fetchNotifications = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !currentTeam) return;
    
    setIsLoading(true);
    try {
      const response = await apiService.getNotifications(20, 0); // Get 20 most recent notifications
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Clear notifications on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, currentTeam]);

  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );
      // Update unread count locally
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Don't update frontend state if backend call failed
      throw error; // Re-throw so the component can handle it if needed
    }
  };

  const markAllAsRead = async (): Promise<void> => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Don't update frontend state if backend call failed
      // This prevents the UI from showing notifications as read when they're not
      throw error; // Re-throw so the component can handle it
    }
  };

  const deleteNotification = async (notificationId: string): Promise<void> => {
    // Check if notification was unread before deleting
    const notificationToDelete = notifications.find(n => n.id === notificationId);
    const wasUnread = notificationToDelete && !notificationToDelete.isRead;
    
    try {
      await apiService.deleteNotification(notificationId);
      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
      // Update unread count if deleted notification was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Optimistically update for better UX
      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  };

  // Fetch notifications when user is authenticated and team is selected
  useEffect(() => {
    if (isAuthenticated && currentTeam) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, currentTeam, fetchNotifications]);

  // Set up polling for new notifications (every 2 minutes)
  useEffect(() => {
    if (!isAuthenticated || !currentTeam) return;

    const interval = setInterval(() => {
      fetchNotifications();
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated, currentTeam, fetchNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};